import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import logo from "../assets/images/icon.png";
import { useAuth } from "../context/AuthContext";

const navLinks = [
  { label: "Home", to: "/", sectionId: null },
  { label: "Services", to: "/services", sectionId: "services" },
  { label: "Custom booking", to: "/custom-booking", sectionId: null },
  { label: "Blog", to: "/blog", sectionId: "blog" },
  { label: "About us", to: "/about-us", sectionId: "about" },
  { label: "How it works", to: "/how-it-works", sectionId: "how-it-works" },
  { label: "FAQ", to: "/faq", sectionId: "faq" },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, openModal, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, link) => {
    if (!link.sectionId) {
      // "Home" link — if already on home, just scroll to top
      if (location.pathname === "/") {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        setMenuOpen(false);
      }
      return;
    }
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(link.sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      setMenuOpen(false);
    }
  };

  return (
    <header
      className={`w-full sticky top-0 z-50 bg-white transition-all duration-300 ${
        scrolled
          ? "shadow-md border-b border-gray-100"
          : "border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <img src={logo} alt="Logo" className="w-14" />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end
              onClick={(e) => handleNavClick(e, link)}
              className={({ isActive }) =>
                `font-manrope-medium text-base transition-colors ${
                  isActive
                    ? "text-black font-manrope-semibold"
                    : "text-secondary hover:text-black"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <Link
              to="/account"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-manrope-bold text-sm text-white uppercase">
                    {(user.name || user.email || "U")[0]}
                  </span>
                )}
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-manrope-semibold text-sm text-black">
                  {user.name || "User"}
                </span>
                <span className="font-manrope-regular text-xs text-secondary">
                  {user.email}
                </span>
              </div>
            </Link>
          ) : (
            <>
              <button
                onClick={() => openModal("sign-in")}
                className="font-manrope-semibold text-base text-gray-700 hover:text-primary transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => openModal("sign-up")}
                className="font-manrope-semibold text-base text-white bg-primary hover:opacity-90 transition-opacity px-5 py-2 rounded-[12px]"
              >
                Sign up
              </button>
            </>
          )}
        </div>

        {/* Mobile: hamburger */}
        <div className="flex lg:hidden items-center gap-3">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-gray-700"
          >
            {menuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-4">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end
                onClick={(e) => {
                  handleNavClick(e, link);
                  setMenuOpen(false);
                }}
                className={({ isActive }) =>
                  `font-manrope-medium text-base py-2 transition-colors ${
                    isActive
                      ? "text-black font-manrope-semibold"
                      : "text-secondary hover:text-black"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex flex-col gap-3 pt-2 border-t border-gray-100">
            {user ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-manrope-bold text-sm text-white uppercase">
                        {(user.name || user.email || "U")[0]}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="font-manrope-semibold text-sm text-black truncate">
                      {user.name || "My account"}
                    </span>
                    <span className="font-manrope-regular text-xs text-secondary truncate">
                      {user.email}
                    </span>
                  </div>
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                  className="font-manrope-semibold text-base text-gray-700 hover:text-primary transition-colors text-left"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    openModal("sign-in");
                    setMenuOpen(false);
                  }}
                  className="font-manrope-semibold text-base text-gray-700 hover:text-primary transition-colors text-left"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    openModal("sign-up");
                    setMenuOpen(false);
                  }}
                  className="font-manrope-semibold text-base text-white bg-primary hover:opacity-90 transition-opacity px-5 py-2 rounded-[12px] text-center"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
